import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../lib/tenant';
import { useAuth } from '../lib/auth';
import { SignInForm } from '../features/login/SignInForm';
import { SentState } from '../features/login/SentState';
import { BlockedState } from '../features/login/BlockedState';
import type { LoginState } from '../features/login/types';

const DEFAULT_POST_LOGIN_PATH = '/opportunities';

/**
 * Login del Client Portal (SW-310).
 *
 * State machine local:
 *   signin   →  pantalla inicial con OAuth (Google + Microsoft) + form email/password
 *               + toggle a magic link
 *   sent     →  post magic-link request — confirmación "revisa tu correo"
 *   blocked  →  el dominio del email no está autorizado para este client
 *
 * Auth flow:
 *   1. Lee `?accessLinkToken=<guid>` del query — required, viene del share link
 *      que el recruiter le envió al cliente.
 *   2. Si ya hay sesión activa al mount (useAuth.isAuthenticated), redirige al
 *      `?returnUrl=...` (sanitizado anti open-redirect) o a `/opportunities`.
 *   3. Tras login exitoso (cualquier método), el componente respectivo invoca
 *      `onAuthenticated` → setea el flag local → useEffect redirige cuando el
 *      próximo /me query devuelve isAuthenticated=true.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { tenant, isLoading: tenantLoading, error: tenantError } = useTenant();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const accessLinkToken = searchParams.get('accessLinkToken');
  const [state, setState] = useState<LoginState>({ kind: 'signin' });
  const [expiredBanner, setExpiredBanner] = useState(false);

  // Surface "sesión expirada" si ProtectedRoute redirigió con state.expired.
  useEffect(() => {
    const navState = location.state as { expired?: boolean } | null;
    if (navState?.expired) setExpiredBanner(true);
  }, [location.state]);

  // Si el user ya está autenticado al mount (refresh con cookie viva) o tras
  // un login exitoso (invalidate /me dispara este re-render), redirigir.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const rawReturnUrl = searchParams.get('returnUrl');
    const safeReturnUrl =
      rawReturnUrl && rawReturnUrl.startsWith('/') && !rawReturnUrl.startsWith('//')
        ? rawReturnUrl
        : DEFAULT_POST_LOGIN_PATH;
    navigate(safeReturnUrl, { replace: true });
  }, [authLoading, isAuthenticated, searchParams, navigate]);

  if (tenantLoading) {
    return (
      <Box
        data-testid="login-loading"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh"
        }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tenantError || !tenant) {
    return (
      <Box
        data-testid="login-tenant-error"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 3
        }}>
        <Alert severity="warning" sx={{ maxWidth: 440 }}>
          {t('common.error.tenantNotFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      data-testid="login-page"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 4 },
          py: 4,
        }}
      >
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            maxWidth: 440,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
          }}
        >
          <Box
            data-testid="login-tenant-brand"
            sx={{
              pb: 2,
              mb: 3,
              borderBottom: 1,
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            {tenant.logoUrl && (
              <Box
                component="img"
                src={tenant.logoUrl}
                alt={tenant.name}
                sx={{ height: 40, mb: 1 }}
              />
            )}
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {tenant.name}
            </Typography>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              {t('app.name')}
            </Typography>
          </Box>

          {expiredBanner && (
            <Alert
              severity="warning"
              onClose={() => setExpiredBanner(false)}
              sx={{ mb: 2 }}
              data-testid="login-expired-banner"
            >
              {t('auth.expired.message')}
            </Alert>
          )}

          {state.kind === 'signin' && (
            <SignInForm
              accessLinkToken={accessLinkToken}
              onMagicLinkSent={(email) => setState({ kind: 'sent', email })}
              onBlocked={(email, domain) => setState({ kind: 'blocked', email, domain })}
              onAuthenticated={() => {
                // El useEffect de arriba detecta isAuthenticated=true tras el
                // invalidate y redirige.
              }}
            />
          )}
          {state.kind === 'sent' && (
            <SentState email={state.email} onBack={() => setState({ kind: 'signin' })} />
          )}
          {state.kind === 'blocked' && (
            <BlockedState
              email={state.email}
              domain={state.domain}
              onBack={() => setState({ kind: 'signin' })}
            />
          )}
        </Paper>
      </Box>
      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: 11,
          letterSpacing: '0.05em',
        }}
        data-testid="login-footer"
      >
        {t('app.poweredBy')}
      </Box>
    </Box>
  );
}
